// /src/pages/users/UserCreate.tsx
//
// Admin user creation page.
//
// Key rules:
// - Login ID + Email must be duplicate-checked explicitly before creation.
// - Changing Login ID / Email resets the corresponding check status back to "Not checked".
// - "Create" is enabled only when:
//   - required fields are valid (format + passwords match)
//   - both duplicate checks are OK
//   - not saving
//
// Username policy:
// - Allowed: a–z, 0–9, . _ -
// - Length: 4–30 characters

import { useCallback, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';

import {
  Autocomplete,
  Box,
  Button,
  Chip,
  Divider,
  IconButton,
  InputAdornment,
  LinearProgress,
  Paper,
  Stack,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import {
  AutoFixHigh,
  CheckCircle,
  ContentCopy,
  ErrorOutline,
  InfoOutlined,
  Lock,
  LockOpen,
  Save as SaveIcon,
  Search,
} from '@mui/icons-material';

import { ADMIN_USER_GROUPS, UserGroup, UserStatus } from '@models/user';
import { createUser, getUserByEmail, getUserByUsername, type SignUpRequest } from '@apis/users';
import { copyToClipboard } from '@utils/common';
import { USER_GROUP_META } from '@constants/userMeta';

import { useSystemAlert } from '@components/common/systemAlert/systemAlert.context';

type DuplicateCheckStatus = 'NOT_CHECKED' | 'DUPLICATED' | 'OK';
type Option<T> = { label: string; value: T; description?: string };

const OK_CODE = 2000;

const normalize = (v?: string | null) => (v ?? '').trim();
const isValidEmail = (email: string) => /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email);

/**
 * Username rule (keep aligned with backend validation).
 * - lowercase letters / digits / . _ -
 * - 4–30 chars
 */
const USERNAME_RE = /^[a-z0-9._-]{4,30}$/;
const isValidUsername = (v: string) => USERNAME_RE.test(v);
const USERNAME_RULE_TEXT = 'Allowed: a–z, 0–9, . _ - · 4–30 chars';

const ACTION_BTN_SX = {
  whiteSpace: 'nowrap',
  width: { xs: '100%', sm: 140 },
  flex: { sm: '0 0 auto' },
} as const;

/**
 * Inline check button layout:
 * - sm+: aligned with input height (not label)
 * - xs: stacked for better touch UX
 */
const INLINE_CHECK_BTN_SX = {
  whiteSpace: 'nowrap',
  height: 40,
  width: { xs: '100%', sm: 180 },
  flex: { sm: '0 0 auto' },
  mt: { xs: 0, sm: 3 },
} as const;

/**
 * Password strength heuristic (UI feedback only).
 * This is not a security boundary.
 */
const getPasswordStrength = (pw: string) => {
  const v = pw ?? '';
  let score = 0;

  if (v.length >= 8) score += 1;
  if (v.length >= 12) score += 1;
  if (/[A-Z]/.test(v) && /[a-z]/.test(v)) score += 1;
  if (/\d/.test(v)) score += 1;
  if (/[^A-Za-z0-9]/.test(v)) score += 1;

  const clamped = Math.min(4, Math.max(0, score - 1));
  const pct = (clamped / 4) * 100;
  const label = clamped <= 1 ? 'Weak' : clamped === 2 ? 'Okay' : clamped === 3 ? 'Good' : 'Strong';

  return { score: clamped, pct, label };
};

/**
 * Generates a mixed password for admin convenience.
 * This does not replace proper password policies.
 */
const generateStrongPassword = () => {
  const lower = 'abcdefghijklmnopqrstuvwxyz';
  const upper = 'ABCDEFGHIJKLMNOPQRSTUVWXYZ';
  const digits = '0123456789';
  const symbols = '!@#$%^&*()-_=+[]{};:,.?';
  const all = lower + upper + digits + symbols;

  const pick = (s: string) => s[Math.floor(Math.random() * s.length)];
  const base = [pick(lower), pick(upper), pick(digits), pick(symbols), ...Array.from({ length: 12 }, () => pick(all))];

  for (let i = base.length - 1; i > 0; i--) {
    const j = Math.floor(Math.random() * (i + 1));
    [base[i], base[j]] = [base[j], base[i]];
  }
  return base.join('');
};

/**
 * Small at-a-glance badge for duplicate-check readiness.
 * This complements (doesn't replace) field-level helper text.
 */
const StatusChip = ({ status, label }: { status: DuplicateCheckStatus; label: string }) => {
  if (status === 'OK') {
    return <Chip size="small" color="success" icon={<CheckCircle />} label={`${label}: OK`} />;
  }
  if (status === 'DUPLICATED') {
    return <Chip size="small" color="error" icon={<ErrorOutline />} label={`${label}: Duplicated`} />;
  }
  return <Chip size="small" variant="outlined" label={`${label}: Not checked`} />;
};

const UserCreate = () => {
  const navigate = useNavigate();
  const systemAlert = useSystemAlert();

  // Keep the request shape in state so the final payload is straightforward.
  const [form, setForm] = useState<SignUpRequest>({
    username: '',
    password: '',
    name: '',
    position: '',
    group: UserGroup.MANAGER,
    email: '',
    phoneNumber: '',
    status: UserStatus.ACTIVE,
  });
  const [passwordConfirm, setPasswordConfirm] = useState('');

  const [showPassword, setShowPassword] = useState(false);
  const [showPasswordConfirm, setShowPasswordConfirm] = useState(false);

  // Mandatory explicit duplicate checks.
  const [usernameStatus, setUsernameStatus] = useState<DuplicateCheckStatus>('NOT_CHECKED');
  const [emailStatus, setEmailStatus] = useState<DuplicateCheckStatus>('NOT_CHECKED');

  const [checkingUsername, setCheckingUsername] = useState(false);
  const [checkingEmail, setCheckingEmail] = useState(false);
  const [saving, setSaving] = useState(false);

  const groupOptions = useMemo<Option<UserGroup>[]>(
    () =>
      ADMIN_USER_GROUPS.map((g) => ({
        value: g,
        label: USER_GROUP_META[g]?.label ?? String(g),
        description: USER_GROUP_META[g]?.description ?? '',
      })),
    [],
  );

  const groupValue = useMemo(
    () => groupOptions.find((o) => o.value === form.group) ?? groupOptions[0],
    [groupOptions, form.group],
  );

  const groupHelper = useMemo(() => USER_GROUP_META[form.group]?.description ?? '', [form.group]);

  const usernameValue = normalize(form.username);
  const usernameFormatInvalid = Boolean(usernameValue) && !isValidUsername(usernameValue);

  const emailValue = normalize(form.email);
  const emailFormatInvalid = Boolean(emailValue) && !isValidEmail(emailValue);

  const pwStrength = useMemo(() => getPasswordStrength(form.password), [form.password]);

  const passwordsMatch =
    form.password.length > 0 && passwordConfirm.length > 0 && form.password === passwordConfirm;

  const hasRequiredValues = useMemo(() => {
    if (!normalize(form.username)) return false;
    if (!normalize(form.name)) return false;
    if (!normalize(form.password)) return false;
    if (!normalize(passwordConfirm)) return false;
    if (!normalize(form.position)) return false;
    if (!normalize(form.email)) return false;
    if (!normalize(form.phoneNumber)) return false;
    if (!form.group) return false;

    if (usernameFormatInvalid) return false;
    if (emailFormatInvalid) return false;
    if (!passwordsMatch) return false;

    return true;
  }, [emailFormatInvalid, form, passwordConfirm, passwordsMatch, usernameFormatInvalid]);

  const canCreate = useMemo(() => {
    if (saving) return false;
    if (!hasRequiredValues) return false;
    if (usernameStatus !== 'OK') return false;
    if (emailStatus !== 'OK') return false;
    return true;
  }, [saving, hasRequiredValues, usernameStatus, emailStatus]);

  const createDisabledReason = useMemo(() => {
    if (saving) return '';
    if (!hasRequiredValues) return 'Fill in all required fields.';
    if (usernameStatus !== 'OK') return 'Run "Check ID" first.';
    if (emailStatus !== 'OK') return 'Run "Check Email" first.';
    return '';
  }, [saving, hasRequiredValues, usernameStatus, emailStatus]);

  // Changing Login ID / Email invalidates the last check result.
  const onUsernameChanged = useCallback((v: string) => {
    setUsernameStatus('NOT_CHECKED');
    setForm((p) => ({ ...p, username: v }));
  }, []);

  const onEmailChanged = useCallback((v: string) => {
    setEmailStatus('NOT_CHECKED');
    setForm((p) => ({ ...p, email: v }));
  }, []);

  const handleCheckUsername = useCallback(async () => {
    const username = normalize(form.username);

    if (!username) {
      systemAlert.info('Enter Login ID first.');
      return;
    }
    if (!isValidUsername(username)) {
      systemAlert.info(`Invalid Login ID. ${USERNAME_RULE_TEXT}`);
      return;
    }

    try {
      setCheckingUsername(true);
      const { data } = await getUserByUsername(username);

      if ((data ?? []).length === 0) {
        setUsernameStatus('OK');
        systemAlert.success('Login ID is available.');
      } else {
        setUsernameStatus('DUPLICATED');
        systemAlert.error('Login ID is already in use.');
      }
    } catch {
      systemAlert.error('Failed to check Login ID.');
    } finally {
      setCheckingUsername(false);
    }
  }, [form.username, systemAlert]);

  const handleCheckEmail = useCallback(async () => {
    const email = normalize(form.email);

    if (!email) {
      systemAlert.info('Enter Email first.');
      return;
    }
    if (!isValidEmail(email)) {
      systemAlert.info('Invalid email format.');
      return;
    }

    try {
      setCheckingEmail(true);
      const { data } = await getUserByEmail(email);

      if ((data ?? []).length === 0) {
        setEmailStatus('OK');
        systemAlert.success('Email is available.');
      } else {
        setEmailStatus('DUPLICATED');
        systemAlert.error('Email is already in use.');
      }
    } catch {
      systemAlert.error('Failed to check Email.');
    } finally {
      setCheckingEmail(false);
    }
  }, [form.email, systemAlert]);

  const handleGeneratePassword = useCallback(() => {
    const next = generateStrongPassword();
    setForm((p) => ({ ...p, password: next }));
    setPasswordConfirm(next);
    systemAlert.success('Strong password generated.');
  }, [systemAlert]);

  const handleCopyPassword = useCallback(() => {
    const pw = form.password ?? '';
    if (!normalize(pw)) return;

    copyToClipboard(pw);
    systemAlert.success('Password copied.');
  }, [form.password, systemAlert]);

  const handleCreate = useCallback(async () => {
    if (!canCreate) return;

    try {
      setSaving(true);

      const payload: SignUpRequest = {
        ...form,
        username: normalize(form.username),
        name: normalize(form.name),
        position: normalize(form.position),
        email: normalize(form.email),
        phoneNumber: normalize(form.phoneNumber),
      };

      const res: any = await createUser(payload);
      const ok = res?.code === OK_CODE || String(res?.message ?? '').toUpperCase() === 'OK';

      if (!ok) {
        systemAlert.error(String(res?.message ?? 'Failed to create user.'));
        return;
      }

      systemAlert.success('User created.');
      navigate('/users');
    } catch {
      systemAlert.error('Failed to create user.');
    } finally {
      setSaving(false);
    }
  }, [canCreate, form, navigate, systemAlert]);

  return (
    <Box sx={{ py: 2, maxWidth: 900, width: '100%', mr: 'auto' }}>
      <Stack spacing={3} sx={{ minWidth: 0 }}>
        <Paper variant="outlined" sx={{ p: { xs: 2, sm: 2.5 }, borderRadius: 2 }}>
          <Stack direction="row" spacing={1} flexWrap="wrap">
            <StatusChip status={usernameStatus} label="ID" />
            <StatusChip status={emailStatus} label="Email" />
          </Stack>
        </Paper>

        <Paper variant="outlined" sx={{ p: { xs: 2, sm: 3 }, borderRadius: 2 }}>
          <Stack spacing={2.5}>
            <Typography variant="subtitle1" fontWeight={900}>
              Identity
            </Typography>

            <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.25} alignItems="stretch">
              <Box sx={{ flex: 1, minWidth: 0 }}>
                <TextField
                  label="Login ID"
                  value={form.username}
                  size="small"
                  fullWidth
                  onChange={(e) => onUsernameChanged(e.target.value)}
                  placeholder="e.g. partner_admin01"
                  error={usernameStatus === 'DUPLICATED' || usernameFormatInvalid}
                  helperText={
                    usernameFormatInvalid
                      ? `Invalid Login ID. ${USERNAME_RULE_TEXT}`
                      : usernameStatus === 'NOT_CHECKED'
                        ? `${USERNAME_RULE_TEXT} · Run "Check ID".`
                        : usernameStatus === 'DUPLICATED'
                          ? 'This Login ID is already in use.'
                          : 'Login ID is available.'
                  }
                />
              </Box>

              <Button
                variant="outlined"
                size="small"
                startIcon={<Search />}
                onClick={handleCheckUsername}
                disabled={checkingUsername || !normalize(form.username) || usernameFormatInvalid}
                sx={INLINE_CHECK_BTN_SX}
              >
                {checkingUsername ? 'Checking…' : 'Check ID'}
              </Button>
            </Stack>

            <TextField
              label="Name"
              value={form.name}
              size="small"
              fullWidth
              onChange={(e) => setForm((p) => ({ ...p, name: e.target.value }))}
              placeholder="Enter name"
            />

            <TextField
              label="Position"
              value={form.position}
              size="small"
              fullWidth
              onChange={(e) => setForm((p) => ({ ...p, position: e.target.value }))}
              placeholder="Enter position"
            />

            <Divider />

            <Typography variant="subtitle1" fontWeight={900}>
              Authentication
            </Typography>

            <TextField
              label="Password"
              value={form.password}
              size="small"
              fullWidth
              type={showPassword ? 'text' : 'password'}
              onChange={(e) => setForm((p) => ({ ...p, password: e.target.value }))}
              placeholder="Enter password"
              InputProps={{
                endAdornment: (
                  <InputAdornment position="end">
                    <Tooltip title={showPassword ? 'Hide' : 'Show'}>
                      <IconButton size="small" onClick={() => setShowPassword((v) => !v)} edge="end">
                        {showPassword ? <LockOpen fontSize="small" /> : <Lock fontSize="small" />}
                      </IconButton>
                    </Tooltip>

                    <Tooltip title="Copy">
                      <span>
                        <IconButton
                          size="small"
                          onClick={handleCopyPassword}
                          disabled={!normalize(form.password)}
                          edge="end"
                        >
                          <ContentCopy fontSize="small" />
                        </IconButton>
                      </span>
                    </Tooltip>
                  </InputAdornment>
                ),
              }}
            />

            <TextField
              label="Confirm Password"
              value={passwordConfirm}
              size="small"
              fullWidth
              type={showPasswordConfirm ? 'text' : 'password'}
              onChange={(e) => setPasswordConfirm(e.target.value)}
              placeholder="Re-enter password"
              error={Boolean(passwordConfirm) && !passwordsMatch}
              helperText={Boolean(passwordConfirm) && !passwordsMatch ? 'Passwords do not match.' : ' '}
              InputProps={{
                endAdornment: (
                  <InputAdornment position="end">
                    <Tooltip title={showPasswordConfirm ? 'Hide' : 'Show'}>
                      <IconButton
                        size="small"
                        onClick={() => setShowPasswordConfirm((v) => !v)}
                        edge="end"
                      >
                        {showPasswordConfirm ? <LockOpen fontSize="small" /> : <Lock fontSize="small" />}
                      </IconButton>
                    </Tooltip>
                  </InputAdornment>
                ),
              }}
            />

            <Stack spacing={1.25}>
              <Stack direction="row" spacing={1} alignItems="center">
                <Typography variant="body2" color="text.secondary">
                  Strength:
                </Typography>
                <Chip
                  size="small"
                  label={pwStrength.label}
                  color={pwStrength.label === 'Strong' ? 'success' : pwStrength.label === 'Good' ? 'primary' : 'default'}
                  variant={pwStrength.label === 'Weak' ? 'outlined' : 'filled'}
                  sx={{ fontWeight: 800 }}
                />
              </Stack>

              <LinearProgress variant="determinate" value={pwStrength.pct} sx={{ height: 8, borderRadius: 99 }} />

              <Button
                variant="outlined"
                size="small"
                startIcon={<AutoFixHigh />}
                onClick={handleGeneratePassword}
                sx={{
                  whiteSpace: 'nowrap',
                  height: 40,
                  width: { xs: '100%', sm: 200 },
                  alignSelf: 'flex-end',
                }}
              >
                Generate strong
              </Button>
            </Stack>

            <Divider />

            <Typography variant="subtitle1" fontWeight={900}>
              Role & Permissions
            </Typography>

            <Autocomplete
              options={groupOptions}
              value={groupValue}
              disableClearable
              onChange={(_e, opt) => setForm((p) => ({ ...p, group: opt.value }))}
              isOptionEqualToValue={(a, b) => a.value === b.value}
              renderInput={(params) => <TextField {...params} label="Group" size="small" helperText={groupHelper || ' '} />}
              renderOption={(props, opt) => (
                <li {...props} key={String(opt.value)}>
                  <Box sx={{ display: 'flex', flexDirection: 'column' }}>
                    <Typography variant="body2" fontWeight={800}>
                      {opt.label}
                    </Typography>
                    {opt.description ? (
                      <Typography variant="caption" color="text.secondary">
                        {opt.description}
                      </Typography>
                    ) : null}
                  </Box>
                </li>
              )}
            />

            <Divider />

            <Typography variant="subtitle1" fontWeight={900}>
              Contact
            </Typography>

            <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.25} alignItems="stretch">
              <Box sx={{ flex: 1, minWidth: 0 }}>
                <TextField
                  label="Email"
                  value={form.email}
                  size="small"
                  fullWidth
                  onChange={(e) => onEmailChanged(e.target.value)}
                  placeholder="e.g. admin@company.com"
                  error={emailStatus === 'DUPLICATED' || emailFormatInvalid}
                  helperText={
                    emailFormatInvalid
                      ? 'Invalid email format.'
                      : emailStatus === 'NOT_CHECKED'
                        ? 'Run "Check Email".'
                        : emailStatus === 'DUPLICATED'
                          ? 'This email is already in use.'
                          : 'Email is available.'
                  }
                />
              </Box>

              <Button
                variant="outlined"
                size="small"
                startIcon={<Search />}
                onClick={handleCheckEmail}
                disabled={checkingEmail || !normalize(form.email) || emailFormatInvalid}
                sx={INLINE_CHECK_BTN_SX}
              >
                {checkingEmail ? 'Checking…' : 'Check Email'}
              </Button>
            </Stack>

            <TextField
              label="Phone"
              value={form.phoneNumber}
              size="small"
              fullWidth
              onChange={(e) => setForm((p) => ({ ...p, phoneNumber: e.target.value }))}
              placeholder="Enter phone"
            />

            <Paper
              variant="outlined"
              sx={(theme) => ({
                p: 1.5,
                borderRadius: 2,
                display: 'flex',
                gap: 1,
                alignItems: 'flex-start',
                borderColor: theme.palette.info.main,
                backgroundColor: theme.palette.mode === 'light' ? 'rgba(2, 136, 209, 0.06)' : 'transparent',
              })}
            >
              <InfoOutlined fontSize="small" color="info" />
              <Typography variant="body2" color="text.secondary">
                Status can be changed later in the Edit page.
              </Typography>
            </Paper>

            <Divider />

            <Stack
              direction={{ xs: 'column', sm: 'row' }}
              spacing={1.25}
              justifyContent="flex-end"
              alignItems={{ xs: 'stretch', sm: 'center' }}
            >
              <Button variant="outlined" onClick={() => navigate(-1)} sx={ACTION_BTN_SX}>
                Cancel
              </Button>

              <Tooltip title={createDisabledReason} disableHoverListener={canCreate || !createDisabledReason}>
                <Box component="span" sx={{ width: { xs: '100%', sm: 140 }, flex: { sm: '0 0 auto' } }}>
                  <Button
                    variant="contained"
                    onClick={handleCreate}
                    disabled={!canCreate}
                    startIcon={<SaveIcon />}
                    sx={ACTION_BTN_SX}
                  >
                    {saving ? 'Creating…' : 'Create'}
                  </Button>
                </Box>
              </Tooltip>
            </Stack>
          </Stack>
        </Paper>
      </Stack>
    </Box>
  );
};

export default UserCreate;
